// PATH: src/app/providers/ThemeProvider.jsx
import React, { createContext, useContext, useReducer, useMemo, useCallback, useEffect } from "react";

const ThemeContext = createContext(undefined);

const THEME_ACTIONS = {
    TOGGLE_DARK: "THEME/TOGGLE_DARK",
    SET_DARK: "THEME/SET_DARK",
};

const STORAGE_KEY = "grupoarca-darkmode";

function themeReducer(state, action) {
    switch (action.type) {
        case THEME_ACTIONS.TOGGLE_DARK:
            return { ...state, darkMode: !state.darkMode };
        case THEME_ACTIONS.SET_DARK:
            return { ...state, darkMode: action.payload };
        default:
            return state;
    }
}

function getInitialDarkMode() {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored !== null) {
            return JSON.parse(stored);
        }
    } catch (err) {
        console.warn("Could not read theme from localStorage:", err);
    }

    if (typeof window !== "undefined" && window.matchMedia) {
        return window.matchMedia("(prefers-color-scheme: dark)").matches;
    }

    return false;
}

export function ThemeProvider({ children }) {
    const [state, dispatch] = useReducer(themeReducer, { darkMode: getInitialDarkMode() });

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(state.darkMode));
        } catch (err) {
            console.warn("Could not persist theme to localStorage:", err);
        }
    }, [state.darkMode]);

    const toggleDarkMode = useCallback(() => {
        dispatch({ type: THEME_ACTIONS.TOGGLE_DARK });
    }, []);

    const setDarkMode = useCallback((value) => {
        dispatch({ type: THEME_ACTIONS.SET_DARK, payload: value });
    }, []);

    const value = useMemo(
        () => ({
            darkMode: state.darkMode,
            toggleDarkMode,
            setDarkMode,
        }),
        [state.darkMode, toggleDarkMode, setDarkMode]
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error("useTheme must be used within ThemeProvider");
    }
    return context;
}